import React, { useEffect, useState } from "react";
import axios from "axios";
import { authHeaders } from "../utils/driverPrivacy";
import { useNavigate } from "react-router-dom";
import "../styles/ContestsPage.css";

export const CountdownTimer = ({ endDate, compact = false }) => {
        const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        const [isExpired, setIsExpired] = useState(false);

        useEffect(() => {
                const calculateTimeLeft = () => {
                        const end = new Date(endDate).getTime();
                        const now = new Date().getTime();
                        const difference = end - now;

                        if (difference <= 0) {
                                setIsExpired(true);
                                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                                return;
                        }

                        setTimeLeft({
                                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                                hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                                minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
                                seconds: Math.floor((difference % (1000 * 60)) / 1000),
                        });
                };

                calculateTimeLeft();
                const interval = setInterval(calculateTimeLeft, 1000);
                return () => clearInterval(interval);
        }, [endDate]);

        if (isExpired) {
                return <span className="countdown-expired">Contest ended</span>;
        }

        if (compact) {
                return (
                        <span className="countdown-compact">
                                {timeLeft.days > 0 && <span>{timeLeft.days}d </span>}
                                {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
                        </span>
                );
        }

        return (
                <div className="countdown-timer">
                        <span className="countdown-label">Time remaining:</span>
                        <div className="countdown-units">
                                {timeLeft.days > 0 && (
                                        <div className="countdown-unit">
                                                <span className="countdown-value">{timeLeft.days}</span>
                                                <span className="countdown-unit-label">days</span>
                                        </div>
                                )}
                                <div className="countdown-unit">
                                        <span className="countdown-value">{String(timeLeft.hours).padStart(2, "0")}</span>
                                        <span className="countdown-unit-label">hrs</span>
                                </div>
                                <div className="countdown-unit">
                                        <span className="countdown-value">{String(timeLeft.minutes).padStart(2, "0")}</span>
                                        <span className="countdown-unit-label">min</span>
                                </div>
                                <div className="countdown-unit">
                                        <span className="countdown-value">{String(timeLeft.seconds).padStart(2, "0")}</span>
                                        <span className="countdown-unit-label">sec</span>
                                </div>
                        </div>
                </div>
        );
};

const formatLapTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = (seconds % 60).toFixed(3);
        return `${mins}:${secs.padStart(6, "0")}`;
};

const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                timeZone: "UTC",
        });
};

const StatusBadge = ({ status }) => {
        const statusStyles = {
                active: { backgroundColor: "#28a745", color: "#fff" },
                upcoming: { backgroundColor: "#f1c40f", color: "#000" },
                completed: { backgroundColor: "#6c757d", color: "#fff" },
        };

        return (
                <span className="contest-status-badge" style={statusStyles[status] || statusStyles.upcoming}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
        );
};

export const ContestsPage = () => {
        const navigate = useNavigate();
        const [contests, setContests] = useState([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [statusFilter, setStatusFilter] = useState("all");

        useEffect(() => {
                const fetchContests = async () => {
                        setLoading(true);
                        setError(null);
                        try {
                                const endpoint =
                                        statusFilter === "all" ? "https://iracing6-backend.herokuapp.com/api/contests" : `https://iracing6-backend.herokuapp.com/api/contests?status=${statusFilter}`;
                                const response = await axios.get(endpoint, { headers: authHeaders() });
                                const contestsArray = Array.isArray(response.data) ? response.data : [response.data];
                                const uniqueContests = contestsArray.filter((contest, index, self) =>
                                        index === self.findIndex((c) => c._id === contest._id)
                                );
                                setContests(uniqueContests);
                        } catch (err) {
                                console.error("Error fetching contests:", err);
                                setError(err.response?.data?.message || err.message);
                        }
                        setLoading(false);
                };
                fetchContests();
        }, [statusFilter]);

        const handleStandingClick = (entry) => {
                if (entry.telemetryLapId) {
                        navigate("/telemetry/compare", {
                                state: {
                                        lap: {
                                                lapId: entry.telemetryLapId,
                                                lapTime: entry.value,
                                                lapNumber: entry.lapNumber || 1,
                                        },
                                        session: {
                                                car: entry.car,
                                                track: entry.track,
                                                driverName: entry.driverName,
                                        },
                                },
                        });
                }
        };

        const getRankColor = (rank) => {
                if (rank === 1) return "#FFD700";
                if (rank === 2) return "#C0C0C0";
                if (rank === 3) return "#CD7F32";
                return "#dddddd";
        };

        return (
                <div className="contests-page">
                        <div className="contests-container">
                                <h1 className="contests-title">Contests</h1>
                                <p className="contests-subtitle"></p>

                                <div className="contests-filter-buttons">
                                        <button className={statusFilter === "all" ? "filter-button active" : "filter-button"} onClick={() => setStatusFilter("all")}>
                                                All
                                        </button>
                                        <button className={statusFilter === "active" ? "filter-button active" : "filter-button"} onClick={() => setStatusFilter("active")}>
                                                Active
                                        </button>
                                        <button className={statusFilter === "upcoming" ? "filter-button active" : "filter-button"} onClick={() => setStatusFilter("upcoming")}>
                                                Upcoming
                                        </button>
                                        <button className={statusFilter === "completed" ? "filter-button active" : "filter-button"} onClick={() => setStatusFilter("completed")}>
                                                Completed
                                        </button>
                                </div>

                                {loading ? (
                                        <div className="contests-loading">
                                                <div className="spinner"></div>
                                        </div>
                                ) : error ? (
                                        <div className="contests-error">
                                                <p>Error loading contests: {error}</p>
                                        </div>
                                ) : contests.length === 0 ? (
                                        <div className="contests-empty">
                                                <p>No contests found.</p>
                                        </div>
                                ) : (
                                        <div className="contests-list">
                                                {contests.map((contest) => (
                                                        <div key={contest._id} className="contest-card">
                                                                <div className="contest-header">
                                                                        <div className="contest-header-left">
                                                                                <h2 className="contest-name">{contest.name}</h2>
                                                                                <StatusBadge status={contest.status} />
                                                                        </div>
                                                                        <div className="contest-dates">
                                                                                {formatDate(contest.startDate)} - {formatDate(contest.endDate)}
                                                                        </div>
                                                                </div>

                                                                {contest.status === "active" && (
                                                                        <div className="contest-countdown-section">
                                                                                <CountdownTimer endDate={contest.endDate} />
                                                                        </div>
                                                                )}

                                                                <div className="contest-section">
                                                                        <span className="section-label">Goal</span>
                                                                        <span className="section-content">The fastest lap time wins!</span>
                                                                </div>

                                                                <div className="contest-section">
                                                                        <span className="section-label">Rules</span>
                                                                        <ul className="section-rules">
                                                                                <li>
                                                                                        You may use any car and any track <u>EXCLUDING Centripetal Circuit.</u>
                                                                                </li>
                                                                                <li>Telemetry must show car on track through a complete lap.</li>
                                                                                <li>The lap must be completed while the contest is active</li>
                                                                                <li>Must not enter pit lane during your fast lap.</li>
                                                                        </ul>
                                                                </div>

                                                                <div className="contest-section">
                                                                        <span className="section-label">Prize</span>
                                                                        <span className="section-content">$20 iRacing credit awarded to the iRacing account with the fastest lap time.</span>
                                                                </div>

                                                                <div className="contest-section">
                                                                        <span className="section-label">How to Enter</span>
                                                                        <span className="section-content">
                                                                                <a href="/telemetry/upload">Drag and drop your telemetry file here</a> and your fastest lap will automatically be entered.
                                                                                <ul className="section-rules">
                                                                                        <li>Only the fastest lap per account is shown below.</li>
                                                                                </ul>
                                                                        </span>
                                                                </div>

                                                                {contest.winner && contest.status === "completed" && (
                                                                        <div className="contest-winner">
                                                                                <span className="winner-label">Winner:</span>
                                                                                <span className="winner-name">{contest.winner.driverName}</span>
                                                                                <span className="winner-time">{formatLapTime(contest.winner.value)}</span>
                                                                        </div>
                                                                )}

                                                                <div className="contest-standings">
                                                                        <h3 className="standings-title">{contest.status === "completed" ? "Final Standings" : "Current Standings"}</h3>
                                                                        <p className="standings-subtitle">Click to view telemetry</p>
                                                                        {contest.standings && contest.standings.length > 0 ? (
                                                                                <div className="standings-list">
                                                                                        {contest.standings.map((entry, index) => (
                                                                                                <div
                                                                                                        key={entry.driverCustId || index}
                                                                                                        className="standing-entry"
                                                                                                        onClick={() => handleStandingClick(entry)}
                                                                                                        style={{ cursor: entry.telemetryLapId ? "pointer" : "default" }}
                                                                                                >
                                                                                                        <div className="standing-left">
                                                                                                                <div className="standing-rank" style={{ color: getRankColor(entry.rank) }}>
                                                                                                                        {entry.rank}
                                                                                                                </div>
                                                                                                                <div className="standing-info">
                                                                                                                        <div className="standing-driver">{entry.driverName}</div>
                                                                                                                        <div className="standing-details">
                                                                                                                                {entry.car} @ {entry.track}
                                                                                                                        </div>
                                                                                                                </div>
                                                                                                        </div>
                                                                                                        <div className="standing-time">{formatLapTime(entry.value)}</div>
                                                                                                </div>
                                                                                        ))}
                                                                                </div>
                                                                        ) : (
                                                                                <p className="no-standings">No entries yet. Be the first to set a time!</p>
                                                                        )}
                                                                </div>
                                                        </div>
                                                ))}
                                        </div>
                                )}
                        </div>
                </div>
        );
};
